import { useEffect, useState } from 'react';
import { Sparkles, MessageCircle } from 'lucide-react';
import { useAuth } from '@/App';

/**
 * WingmanTriggerPrompts
 * ---------------------
 * Context-aware question launcher that appears below the "How to Read" guides
 * on Snapshot, Growth vs Model, and Spreadsheet Uploader.
 *
 * Rules (see PRD — Feb 2026 trigger-prompt spec):
 *  - SSOT source of truth is `window.__pfd_active_record_ssot`. No detection
 *    logic is recomputed client-side here.
 *  - Hero prompt: always "Explain what's happening in this record".
 *  - Secondary prompts are generated from SSOT state. Maximum 5 total.
 *  - Entire block is hidden if no active SSOT / record context.
 *  - On click, the component dispatches window event `pfd:wingman:ask` with the
 *    fully composed question (context header + question). `WingmanChat.js`
 *    listens for it, opens the panel, and auto-sends — no user Send click.
 *  - We also write a tiny click-count tally to `localStorage` under
 *    `pfd_wingman_prompt_clicks` for future UX iteration. Lightweight, no
 *    network calls.
 *
 * The `moduleName` prop labels which analysis surface is being viewed so the
 * context block matches what the user sees on screen.
 */

const SSOT_POLL_MS = 600;
const STORAGE_KEY = 'pfd_wingman_prompt_clicks';

function readSsot() {
  if (typeof window === 'undefined') return null;
  return window.__pfd_active_record_ssot || null;
}

/**
 * Build the context block that is prepended to every question so Wingman
 * answers remain locked to the numbers the user is viewing. Only renders
 * fields that are actually present on the SSOT — no placeholders.
 */
function buildContextHeader(ssot, moduleName) {
  const parts = [];
  if (ssot?.record_name) parts.push(`Record: ${ssot.record_name}`);
  if (moduleName) parts.push(`Module: ${moduleName}`);
  if (ssot?.confidenceLabel) parts.push(`Confidence: ${ssot.confidenceLabel}`);
  if (ssot?.detectionType && ssot.detectionType !== 'none') {
    parts.push(`Detection: ${ssot.detectionType}`);
  } else if (ssot?.detectionType === 'none') {
    parts.push('Detection: none');
  }
  if (ssot?.missingFishRangeLabel) parts.push(`Range: ${ssot.missingFishRangeLabel}`);
  if (typeof ssot?.hasConflict === 'boolean') {
    parts.push(`Conflict: ${ssot.hasConflict ? 'yes' : 'no'}`);
  }
  if (ssot?.hasAnomaly === true) parts.push('Anomaly: present');
  return parts.join(' | ');
}

/**
 * MODULE-SPECIFIC PROMPT ALLOWLISTS
 * ────────────────────────────────────────────────────────────────────────────
 * Prompt generation is gated by BOTH moduleName and record SSOT state. No
 * prompt may appear just because an SSOT field is set if it is not valid for
 * the active module. Each module exposes its own function; there is NO
 * shared/generic pool. Fail closed, not open.
 *
 * Terminology discipline — only these abbreviations are allowed anywhere in
 * this file: FCR (Feed Conversion Ratio), SGR (Specific Growth Rate),
 * SFR (Specific Feed Rate), RFI (Relative Feed Index). Any other KPI
 * abbreviation is forbidden and must never appear in a prompt, label, or
 * payload.
 *
 * Each function returns an ordered list with the module-specific hero first,
 * followed by up to 4 context-aware chips. Maximum 5 total. Deduped.
 */

const HERO_PROMPTS = {
  Snapshot:           'Explain what this snapshot result means',
  'Growth vs Model':  "Explain what's happening in this growth curve",
  'Spreadsheet Uploader': 'Explain the performance of this cage',
};

function makePusher(out, seen) {
  return (q) => {
    if (!q) return;
    if (seen.has(q)) return;
    if (out.length >= 5) return;
    seen.add(q);
    out.push(q);
  };
}

function isLowConfidence(ssot) {
  const cl = ssot?.confidenceLabel;
  return cl === 'Low' || cl === 'Indication Only' || cl === 'Uncertain';
}

function hasMissingFishSignal(ssot) {
  if (!ssot) return false;
  return !!ssot.hasMissingFishSignal
    || (!!ssot.missingFishRangeLabel && ssot.missingFishRangeLabel !== 'Within margin');
}

/**
 * Snapshot — single-point FCR analysis.
 * Allowed topics: snapshot FCR signal, confidence, model comparison at
 *   current weight, what to check next before acting.
 * Forbidden: jump detection, multi-sample anomaly questions, RFI deep-dive,
 *   SGR/SFR trajectory analysis, feeding trend across datasets.
 */
export function getSnapshotPrompts(ssot) {
  const out = [];
  const seen = new Set();
  const push = makePusher(out, seen);
  push(HERO_PROMPTS.Snapshot);

  // The snapshot value users most want to explain is why FCR diverged — show
  // this whenever there's ANY signal on the screen.
  if (hasMissingFishSignal(ssot)) {
    push('Why is FCR different from the model here?');
  }
  // Confidence / reliability — always relevant for a single-point read-out.
  push('How reliable is this snapshot signal?');
  // Low-confidence branch — data-improvement question.
  if (isLowConfidence(ssot)) {
    push('What data would improve confidence?');
  }
  // Operational anchor.
  push('What should I check before acting on this?');

  return out;
}

/**
 * Growth vs Model — multi-sample deviation and jump analysis.
 * Allowed topics: growth vs model, jump detection, anomaly samples,
 *   deviation patterns, signal conflicts.
 * Forbidden: RFI questions, feeding-vs-model KPI deep dive, SFR calculator
 *   concepts, Growth Prognosis logic.
 */
export function getGrowthModelPrompts(ssot) {
  const out = [];
  const seen = new Set();
  const push = makePusher(out, seen);
  push(HERO_PROMPTS['Growth vs Model']);

  const detection = ssot?.detectionType || null;
  const anyDeviationSignal = hasMissingFishSignal(ssot);

  if (detection === 'jump') {
    push('Why was a jump detected in this sample?');
  } else if (detection === 'none' && anyDeviationSignal) {
    push('Why was no jump detected despite this deviation?');
  }
  if (ssot?.hasAnomaly === true) {
    push('Why is this sample flagged as an anomaly?');
  }
  // Sampling error vs real loss — always-available interpretive question
  // for any record showing a deviation signal.
  if (anyDeviationSignal) {
    push('Are these changes more likely sampling error or real fish loss?');
  }
  // Operational anchor.
  push('What should I do next based on this signal?');

  return out;
}

/**
 * Spreadsheet Uploader — multi-dataset KPI comparison (FCR / RFI / feeding).
 * Allowed topics: FCR vs model, feeding vs model, RFI analysis, KPI
 *   interpretation, performance drivers (feeding vs growth vs stock).
 * Forbidden: jump detection, anomaly sample prompts, SFR calculator prompts,
 *   Growth Prognosis concepts.
 */
export function getSpreadsheetPrompts(ssot) {
  const out = [];
  const seen = new Set();
  const push = makePusher(out, seen);
  push(HERO_PROMPTS['Spreadsheet Uploader']);

  // FCR divergence question — anchored on the presence of any discrepancy
  // signal (which in Spreadsheet domain maps to FCR-vs-model deviation).
  if (hasMissingFishSignal(ssot)) {
    push('Why is my FCR above or below the model?');
  }
  // Static KPI-education anchor — always relevant for the Spreadsheet domain.
  push('What is RFI analysis?');
  // Feeding-vs-growth interpretive question — gated on an actionability that
  // indicates a real pattern rather than clean data.
  if (ssot?.actionability && ssot.actionability !== 'MONITOR_ONLY') {
    push('Is this discrepancy due to feeding, growth, or stock error?');
  }
  // Operational anchor.
  push('What should I do next based on these KPIs?');

  return out;
}

/**
 * Dispatcher — routes to the module-specific allowlist. If the moduleName is
 * not one of the three supported modules we render NOTHING (fail closed).
 */
export function getPromptsForModule(moduleName, ssot) {
  if (!ssot) return [];
  switch (moduleName) {
    case 'Snapshot':              return getSnapshotPrompts(ssot);
    case 'Growth vs Model':       return getGrowthModelPrompts(ssot);
    case 'Spreadsheet Uploader':  return getSpreadsheetPrompts(ssot);
    default:                      return [];   // unknown module → no prompts
  }
}

/**
 * BACK-COMPAT — retained named export for existing unit tests.
 * Delegates to the Spreadsheet allowlist when a moduleName isn't explicit,
 * because the old generic behaviour was closest to Spreadsheet KPIs.
 * New code should call getPromptsForModule instead.
 */
export function derivePrompts(ssot, moduleName = 'Spreadsheet Uploader') {
  return getPromptsForModule(moduleName, ssot);
}

function trackClick(prompt) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    obj[prompt] = (obj[prompt] || 0) + 1;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (_err) {
    // Best-effort only — storage may be full or disabled.
  }
}

export default function WingmanTriggerPrompts({ moduleName = 'Analysis' }) {
  // Poll the SSOT bridge. We intentionally poll (600ms) instead of subscribing
  // to a specific event because host pages write the bridge in many places
  // (record load, re-run analysis, mode toggles). Polling keeps the component
  // host-agnostic.
  const { user } = useAuth();
  const [ssot, setSsot] = useState(() => readSsot());
  useEffect(() => {
    const read = () => {
      const next = readSsot();
      setSsot((prev) => {
        // Shallow-compare the six fields that drive prompt content to avoid
        // unnecessary re-renders when SSOT is republished identically.
        if (!prev && !next) return prev;
        if (!prev || !next) return next;
        const keys = [
          'record_name', 'confidenceLabel', 'missingFishRangeLabel',
          'hasConflict', 'detectionType', 'hasAnomaly', 'hasMissingFishSignal',
        ];
        const same = keys.every((k) => prev[k] === next[k]);
        return same ? prev : next;
      });
    };
    read();
    const t = setInterval(read, SSOT_POLL_MS);
    return () => clearInterval(t);
  }, []);

  if (!ssot || user?.is_restricted_demo) return null;

  // Dispatch strictly by moduleName — unknown modules yield no prompts
  // (fail closed). Snapshot-only prompts cannot appear on Growth / Spreadsheet
  // and vice versa.
  const prompts = getPromptsForModule(moduleName, ssot);
  if (prompts.length <= 1) return null;   // only hero with no context — hide entirely

  const hero = prompts[0];
  const secondary = prompts.slice(1);

  const askWingman = (question) => {
    const header = buildContextHeader(ssot, moduleName);
    const composed = header
      ? `${header}.\nQuestion: ${question}`
      : question;
    trackClick(question);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pfd:wingman:ask', {
        detail: { text: composed, question, context: header, moduleName },
      }));
    }
  };

  return (
    <div
      data-testid="wingman-trigger-prompts"
      className="mt-3 px-4 py-3 bg-gradient-to-br from-cyan-50 to-slate-50 border border-cyan-200 rounded-lg"
    >
      <div className="flex items-start gap-2 mb-2">
        <div className="mt-0.5 shrink-0 w-6 h-6 rounded-md bg-gradient-to-br from-[#0891b2] to-[#065a73] flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-white" />
        </div>
        <div>
          <h4
            className="text-xs font-semibold text-slate-800"
            data-testid="wingman-trigger-title"
          >
            Not sure what you&rsquo;re seeing? Ask Wingman
          </h4>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Opens Wingman with your current record pre-loaded
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => askWingman(hero)}
        data-testid="wingman-trigger-hero"
        className="w-full bg-[#0891b2] hover:bg-[#06748c] text-white text-sm font-medium rounded-lg px-3 py-2 flex items-center justify-center gap-2 transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
        {hero}
      </button>

      {secondary.length > 0 && (
        <div
          className="mt-2 flex flex-wrap gap-1.5"
          data-testid="wingman-trigger-secondary-row"
        >
          {secondary.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => askWingman(q)}
              data-testid={`wingman-trigger-chip-${q.slice(0, 30).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              className="text-[11px] bg-white border border-slate-200 text-slate-700 rounded-full px-2.5 py-1 hover:border-[#0891b2] hover:text-[#0891b2] hover:bg-[#0891b2]/5 transition-colors"
              title={q}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
